import type { OnboardingStep, TeamConfig } from '../store/onboardingStore';

export type FunnelVariant = 'v1' | 'v2';

const FUNNEL_VARIANT_COOKIE = 'wemove_funnel_variant';
const FUNNEL_VARIANT_STORAGE_KEY = 'wemove_funnel_variant';
/** Legacy sticky key from B0 start — still read as a mirror fallback. */
const LEGACY_AB_STORAGE_KEY = 'wemove_funnel_v2_ab';
const COOKIE_MAX_AGE_SECONDS = 90 * 24 * 60 * 60;

/** True when running on the production admin host. */
export function isProductionHost(): boolean {
    if (typeof window === 'undefined') {
        return false;
    }
    return window.location.hostname === 'app.getwemove.com';
}

function readCookie(name: string): string | null {
    if (typeof document === 'undefined') {
        return null;
    }
    const match = document.cookie.match(new RegExp(`(?:^|; )${name}=([^;]*)`));
    return match ? decodeURIComponent(match[1]) : null;
}

function writeCookie(name: string, value: string, maxAgeSeconds: number): void {
    if (typeof document === 'undefined') {
        return;
    }
    const secure = typeof window !== 'undefined' && window.location.protocol === 'https:'
        ? '; Secure'
        : '';
    document.cookie = `${name}=${encodeURIComponent(value)}; Path=/; Max-Age=${maxAgeSeconds}; SameSite=Lax${secure}`;
}

function normalizeVariant(raw: string | null | undefined): FunnelVariant | null {
    if (raw === 'v1' || raw === 'v2') {
        return raw;
    }
    if (raw === '1') {
        return 'v2';
    }
    if (raw === '0') {
        return 'v1';
    }
    return null;
}

function persistVariant(variant: FunnelVariant): void {
    try {
        writeCookie(FUNNEL_VARIANT_COOKIE, variant, COOKIE_MAX_AGE_SECONDS);
    } catch {
        // ignore cookie write failures (privacy mode)
    }
    try {
        window.localStorage.setItem(FUNNEL_VARIANT_STORAGE_KEY, variant);
        window.localStorage.setItem(LEGACY_AB_STORAGE_KEY, variant === 'v2' ? '1' : '0');
    } catch {
        // ignore storage failures
    }
}

function readStoredVariant(): FunnelVariant | null {
    const fromCookie = normalizeVariant(readCookie(FUNNEL_VARIANT_COOKIE));
    if (fromCookie) {
        return fromCookie;
    }
    try {
        const fromStorage = normalizeVariant(
            window.localStorage.getItem(FUNNEL_VARIANT_STORAGE_KEY)
        );
        if (fromStorage) {
            return fromStorage;
        }
        return normalizeVariant(window.localStorage.getItem(LEGACY_AB_STORAGE_KEY));
    } catch {
        return null;
    }
}

/**
 * Sticky AB assignment. Cookie is primary (90 days); localStorage is a mirror
 * fallback (including the legacy `wemove_funnel_v2_ab` key).
 *
 * Kill switch: unset/`false` REACT_APP_FUNNEL_V2 → everyone v1 (cookies ignored).
 */
export function getFunnelVariant(): FunnelVariant {
    if (typeof window === 'undefined') {
        return 'v1';
    }

    const flag = process.env.REACT_APP_FUNNEL_V2;
    if (flag === 'true') {
        return 'v2';
    }
    if (flag !== 'ab') {
        return 'v1';
    }

    const existing = readStoredVariant();
    if (existing) {
        persistVariant(existing);
        return existing;
    }

    const assigned: FunnelVariant = Math.random() < 0.5 ? 'v2' : 'v1';
    persistVariant(assigned);
    return assigned;
}

/**
 * Sprint B funnel.
 * `true` = 100% V2. `ab` = sticky 50/50. unset/`false` = everyone V1 (kill switch).
 */
export function isFunnelV2Enabled(): boolean {
    return getFunnelVariant() === 'v2';
}

export const FUNNEL_V2_DEFAULT_TEAMS: TeamConfig[] = [
    { name: 'Team Alpha', emoji: '🔥', color: 0, maxMembers: null },
    { name: 'Team Beta', emoji: '⚡', color: 1, maxMembers: null },
];

export const FUNNEL_V2_VISIBLE_STEPS: OnboardingStep[] = [
    'goal-selection',
    'challenge-dates',
    'team-size',
    'competition-type',
    '0',
    'paywall',
    '1',
    'invite-preview',
];

export function getFunnelV2Defaults(competitionType: 'individual' | 'teams' | null): {
    challengePurpose: 'fun';
    challengeType: string;
    prizes: null;
    communicationTool: null;
    teams: TeamConfig[] | null;
    teamJoinMode: 'admin' | 'self' | 'auto' | null;
} {
    const isTeams = competitionType === 'teams';
    return {
        challengePurpose: 'fun',
        challengeType: 'individual',
        prizes: null,
        communicationTool: null,
        teams: isTeams ? FUNNEL_V2_DEFAULT_TEAMS : null,
        teamJoinMode: isTeams ? 'auto' : null,
    };
}
